import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Badge,
  Button,
  ClipboardLabel,
  PermissionsProvider,
  Table,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import type {AdminBusinessReferral} from '@/types';
import {affiliateReferralStatusColors, BusinessReferralStatus} from '@/types';
import {formatDate} from '@/utils/date';

import DisqualifyReferralModal from './DisqualifyReferralModal';

const CELL = 'pe-4';
const ID_CELL = 'max-w-[7rem] overflow-hidden';

const usd = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value || 0);

// The kinds the server stores in selfReferralSignals. Anything else (an older
// or newer kind) falls back to its humanised text.
const SELF_REFERRAL_SIGNAL_LABELS: Record<string, string> = {
  EMAIL: 'Same email as the referrer',
  DOMAIN: 'Same email domain as the referrer',
  PHONE: 'Same phone as the referrer',
};

const signalLabel = (signal: string): string =>
  SELF_REFERRAL_SIGNAL_LABELS[signal] ??
  signal.replace(/_/g, ' ').toLowerCase();

const deadlineFor = (referral: AdminBusinessReferral): string =>
  referral.status === BusinessReferralStatus.Attributed
    ? `Activate by ${formatDate(referral.activationDeadlineAt)}`
    : `Earns until ${formatDate(referral.rewardEndsAt)}`;

const AffiliateReferralsTable = ({
  items,
  onDisqualify,
}: {
  items: AdminBusinessReferral[];
  onDisqualify: (referralId: string, reason: string) => Promise<boolean>;
}) => {
  const [selected, setSelected] = useState<AdminBusinessReferral>();

  return (
    <>
      <Table className="overflow-hidden">
        <Thead>
          <Tr enableHover={false}>
            <Th className={`ps-5 ${CELL}`}>Code</Th>
            <Th className={CELL}>Referrer</Th>
            <Th className={CELL}>Referee</Th>
            <Th className={CELL}>Status</Th>
            <Th className={CELL}>Signed up</Th>
            <Th className={CELL}>Window</Th>
            <Th className={CELL}>Volume</Th>
            <Th className={CELL}>Pending</Th>
            <Th className={CELL}>Paid</Th>
            <Th className={CELL}>Flags</Th>
            <Th>&nbsp;</Th>
          </Tr>
        </Thead>
        <TBody>
          {items.map((referral, index) => (
            <Tr
              key={referral.id}
              className={
                index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow'
              }
            >
              <Td className={`font-mono ps-5 ${CELL}`}>{referral.code}</Td>
              {/* Two 24-character ids would take a third of the table and
                  push the flags and the action off the right edge. Clipped
                  rather than shortened, so the click still copies the whole
                  id: ClipboardLabel copies its text child verbatim. */}
              <Td className={`font-mono text-xs ${CELL} ${ID_CELL}`}>
                <ClipboardLabel>{referral.referrerBusinessId}</ClipboardLabel>
              </Td>
              <Td className={`font-mono text-xs ${CELL} ${ID_CELL}`}>
                <ClipboardLabel>{referral.refereeBusinessId}</ClipboardLabel>
              </Td>
              <Td className={CELL}>
                <Badge
                  label={referral.status}
                  colorScheme={affiliateReferralStatusColors[referral.status]}
                />
              </Td>
              <Td className={CELL}>{formatDate(referral.signedUpAt)}</Td>
              <Td className={CELL}>{deadlineFor(referral)}</Td>
              <Td className={CELL}>{usd(referral.totals.outboundVolumeUSD)}</Td>
              <Td className={CELL}>{usd(referral.totals.pendingRewardUSD)}</Td>
              <Td className={CELL}>{usd(referral.totals.paidRewardUSD)}</Td>
              <Td className={CELL}>
                {referral.selfReferralSignals?.length ? (
                  <div className="flex flex-col items-start gap-1">
                    <Badge
                      label={`${referral.selfReferralSignals.length} flag${
                        referral.selfReferralSignals.length > 1 ? 's' : ''
                      }`}
                      colorScheme="pending"
                    />
                    <ul className="text-xs text-gray-500 dark:text-gray-400">
                      {referral.selfReferralSignals.map((signal) => (
                        <li key={signal}>{signalLabel(signal)}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </Td>
              <Td className={CELL}>
                {referral.status === BusinessReferralStatus.Disqualified ? (
                  <span
                    className="block max-w-[11rem] truncate text-sm text-gray-500 dark:text-gray-400"
                    title={referral.disqualifiedReason}
                  >
                    {referral.disqualifiedReason ?? 'Disqualified'}
                  </span>
                ) : (
                  <PermissionsProvider
                    permission={AfriexAdminPermission.BusinessAffiliateManage}
                  >
                    <Button
                      variant="outline"
                      colorScheme="red"
                      onClick={() => setSelected(referral)}
                      data-testid={`affiliate-referral-disqualify-${referral.id}`}
                    >
                      Disqualify
                    </Button>
                  </PermissionsProvider>
                )}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>

      {selected ? (
        <DisqualifyReferralModal
          isOpen
          referral={selected}
          onClose={() => setSelected(undefined)}
          onConfirm={(reason) => onDisqualify(selected.id, reason)}
        />
      ) : null}
    </>
  );
};

export default AffiliateReferralsTable;
